import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toast'
import { useUpdateContractStatus } from '@/hooks/useContracts'
import {
  CONTRACT_STATUS_COLORS,
  getAllowedContractStatusOptions,
} from '@/lib/contracts'
import { cn } from '@/lib/utils'
import { getApiErrorMessage } from '@/services/api'
import { CONTRACT_STATUS_LABELS, type ContractStatus } from '@/types'

interface Props {
  contractId: string
  currentStatus: ContractStatus
  onTerminate: () => void
  variant?: 'badge' | 'field'
  size?: 'sm' | 'md'
  className?: string
  triggerClassName?: string
}

export function ContractStatusSelect({
  contractId,
  currentStatus,
  onTerminate,
  variant = 'badge',
  size = 'sm',
  className,
  triggerClassName,
}: Props) {
  const { t } = useTranslation(['contracts', 'common', 'enums'])
  const updateStatus = useUpdateContractStatus(contractId)
  const options = useMemo(
    () => getAllowedContractStatusOptions(currentStatus),
    [currentStatus]
  )
  const currentColors = CONTRACT_STATUS_COLORS[currentStatus]
  const isField = variant === 'field'
  const isMd = size === 'md'
  const disabled = updateStatus.isPending || options.length <= 1

  const update = async (status: ContractStatus) => {
    if (status === currentStatus) return
    if (status === 'terminated') {
      onTerminate()
      return
    }

    try {
      await updateStatus.mutateAsync({ status })
      toast({
        variant: 'success',
        title: t('detail.statusUpdate.success'),
      })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('detail.statusUpdate.error')),
      })
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type='button'
          aria-label={t('detail.statusUpdate.label')}
          className={cn(
            'inline-flex shrink-0 items-center justify-center whitespace-nowrap outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed',
            isField
              ? 'h-10 gap-2 rounded-xl border border-[#e7e9ef] bg-white px-4 py-3 text-sm font-medium leading-none text-[#171a20]'
              : isMd
                ? 'h-10 gap-2 rounded-xl border-0 px-4 py-3 text-sm font-medium leading-none'
                : 'h-6 gap-1 rounded-lg border-0 px-2 py-1 text-xs font-medium leading-4',
            triggerClassName,
            className
          )}
          style={
            isField
              ? undefined
              : {
                  backgroundColor: currentColors.bg,
                  color: currentColors.text,
                }
          }
        >
          <span>{CONTRACT_STATUS_LABELS[currentStatus]}</span>
          <ChevronDown
            className={cn(
              'shrink-0',
              isField || isMd ? 'size-4' : 'size-3.5'
            )}
            strokeWidth={2}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        sideOffset={8}
        className='w-[232px] rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0px_1px_8px_-1px_rgba(38,45,66,0.1),0px_1px_4px_-1px_rgba(38,45,66,0.05)]'
      >
        <div className='flex flex-col gap-1'>
          {options.map(status => {
            const selected = status === currentStatus
            return (
              <DropdownMenuItem
                key={status}
                className={cn(
                  'flex cursor-pointer items-start gap-2 rounded-xl border border-[#e7e9ef] px-4 py-2 text-sm font-medium leading-[1.4] text-[#171a20] focus:text-[#171a20]',
                  selected
                    ? 'border-[#ff5a1f] bg-[#fff1ea] focus:bg-[#fff1ea]'
                    : 'bg-white focus:bg-[#f4f5f8]'
                )}
                onSelect={() => update(status)}
              >
                <span
                  className='mt-[7px] size-2 shrink-0 rounded-full'
                  style={{ backgroundColor: CONTRACT_STATUS_COLORS[status].dot }}
                  aria-hidden
                />
                <span className='min-w-0 flex-1'>
                  {CONTRACT_STATUS_LABELS[status]}
                </span>
              </DropdownMenuItem>
            )
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default ContractStatusSelect
